// Предложение установить игру как приложение (PWA). Ненавязчивая плашка в левом верхнем углу:
// только на стартовом экране и в лобби, только если игра ещё не установлена; закрывается крестиком
// и после этого не показывается две недели. Chrome/Android — системный диалог установки по
// событию beforeinstallprompt; iOS Safari события нет — плашка раскрывает подсказку «Поделиться → На экран „Домой“»;
// Android-браузеры без WebAPK (Яндекс Браузер и др.) — плашка ведёт intent-ссылкой в Chrome, где установка настоящая.

const LS_KEY = 'krutagidon-install-dismissed';
const SNOOZE_MS = 14 * 24 * 3600 * 1000;

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
let allowed = false; // разрешает ли текущий экран показывать плашку
let closed = false; // закрыта в этой сессии (или установлена)
let iosHint = false; // плашка раскрыта в подсказку для iOS
let installed = false; // событие appinstalled в этой сессии
let autoInstall = false; // страницу открыли из другого браузера кнопкой «Установить через Chrome» (?install=1) — сразу предложить установку
let autoCb: ((state: 'ready' | 'missing') => void) | null = null;
let installedCb: (() => void) | null = null; // пользователь подтвердил установку в системном диалоге

/** Подсказка для iOS Safari: системного диалога установки там нет */
export const IOS_HINT = 'В Safari нажми <b>Поделиться</b> <span class="install-ic">⎋</span>, затем <b>«На экран „Домой“»</b> — игра откроется как приложение, без адресной строки.';

/** Подсказка для Android-браузеров без настоящей установки PWA (Яндекс Браузер и др.) */
export const CHROME_HINT = 'В этом браузере игра ставится лишь как ярлык с адресной строкой. Открой сайт в <b>Chrome</b> и установи там — приложение будет во весь экран.';

/** Android без WebAPK: «Добавить на главный экран» здесь — ярлык-закладка, а не приложение; ставить надо через Chrome */
function isAndroidNoPwa(): boolean {
  const ua = navigator.userAgent;
  return /Android/.test(ua) && /YaBrowser|YaApp|MiuiBrowser|UCBrowser|Firefox|HuaweiBrowser/.test(ua);
}

/** Android intent-ссылка: открыть текущий адрес (с ?room=… и т.п.) именно в Chrome; без Chrome — его страница в Play Store.
 *  Схема googlechrome:// (Chrome понимает googlechrome://navigate?url=…), а не https — https-intent Яндекс Браузер
 *  перехватывает и открывает сам, чужую схему обязан отдать системе. URL после url= не кодируется: Chrome берёт
 *  хвост строки как есть (проверено на Chrome 151 — закодированный адрес он молча игнорирует). */
export function chromeIntentUrl(): string {
  const u = new URL(location.href);
  u.searchParams.set('install', '1'); // в Chrome сразу открыть окно установки, а не искать плашку заново
  const fallback = encodeURIComponent('https://play.google.com/store/apps/details?id=com.android.chrome');
  return `intent://navigate?url=${u.href}#Intent;scheme=googlechrome;package=com.android.chrome;S.browser_fallback_url=${fallback};end`;
}

/** Что показать после подтверждения установки (значок появится на рабочем столе через несколько секунд) */
export function onInstalled(cb: () => void): void {
  installedCb = cb;
}

/** Что делать, когда страница открыта с ?install=1: 'ready' — системный диалог доступен, 'missing' — браузер установку не предложил */
export function onAutoInstall(cb: (state: 'ready' | 'missing') => void): void {
  autoCb = cb;
}

/** Открыта ли страница уже как установленное приложение */
export function isInstalledPwa(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean };
  return window.matchMedia('(display-mode: fullscreen), (display-mode: standalone)').matches || nav.standalone === true || document.referrer.startsWith('android-app://');
}

function isIosSafari(): boolean {
  const ua = navigator.userAgent;
  const ios = /iPhone|iPad|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return ios && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

function snoozed(): boolean {
  try {
    const t = Number(localStorage.getItem(LS_KEY) ?? 0);
    return t > 0 && Date.now() - t < SNOOZE_MS;
  } catch {
    return false;
  }
}

function dismiss(): void {
  closed = true;
  try {
    localStorage.setItem(LS_KEY, String(Date.now()));
  } catch {
    /* приватный режим — просто не показываем до перезагрузки */
  }
  update();
}

function el(): HTMLElement | null {
  return document.getElementById('install');
}

/** Системный диалог установки: событие пришло и это не браузер без WebAPK (Яндекс Браузер событие шлёт, но ставит лишь ярлык) */
function hasPrompt(): boolean {
  return !!deferred && !isAndroidNoPwa();
}

/** Можно ли предложить установку: системный диалог, iOS Safari или Android-браузер, из которого отправляем в Chrome */
function canOffer(): boolean {
  return hasPrompt() || isIosSafari() || isAndroidNoPwa();
}

function update(): void {
  const box = el();
  if (!box) return;
  const show = allowed && !closed && canInstall();
  document.documentElement.classList.toggle('has-install', show);
  if (!show) {
    box.innerHTML = '';
    box.classList.remove('open');
    return;
  }
  box.classList.add('open');
  if (iosHint) {
    box.innerHTML = `<div class="install-hint">${IOS_HINT}<button class="install-x" data-install="close" aria-label="Закрыть">✕</button></div>`;
  } else if (isAndroidNoPwa()) {
    box.innerHTML = `<a class="install-btn" href="${chromeIntentUrl()}"><span class="install-ic">📲</span><span>Установить через Chrome</span></a><button class="install-x" data-install="close" aria-label="Закрыть">✕</button>`;
  } else {
    box.innerHTML = `<button class="install-btn" data-install="go"><span class="install-ic">📲</span><span>Установить как приложение</span></button><button class="install-x" data-install="close" aria-label="Закрыть">✕</button>`;
  }
}

/** Можно ли сейчас установить игру: не установлена и есть чем предложить (системный диалог или подсказка iOS) */
export function canInstall(): boolean {
  return !installed && canOffer() && !isInstalledPwa();
}

/** Запуск установки: системный диалог ('accepted' — подтвердил, 'prompted' — отказался), подсказка iOS ('ios'),
 *  отправить в Chrome ('chrome') или нечего предлагать ('none') */
export async function requestInstall(): Promise<'accepted' | 'prompted' | 'ios' | 'chrome' | 'none'> {
  if (hasPrompt() && deferred) {
    const ev = deferred;
    deferred = null;
    try {
      await ev.prompt();
    } catch {
      deferred = ev; // браузер не дал показать диалог (нет жеста пользователя) — событие ещё пригодится
      return 'none';
    }
    const choice = await ev.userChoice.catch(() => ({ outcome: 'dismissed' as const }));
    if (choice.outcome === 'accepted') {
      installed = true;
      update();
      installedCb?.();
      return 'accepted';
    }
    dismiss(); // отказался в системном диалоге — плашкой больше не приставать
    update();
    return 'prompted';
  }
  if (isIosSafari()) return 'ios';
  if (isAndroidNoPwa()) return 'chrome';
  return 'none';
}

async function go(): Promise<void> {
  const r = await requestInstall();
  if (r === 'ios') {
    iosHint = true;
    update();
  }
}

/** Сколько экранов показывать плашку: вызывается из render() — только старт и лобби, в партии не мешать */
export function setInstallBannerAllowed(v: boolean): void {
  if (allowed === v) return;
  allowed = v;
  update();
}

/** Подключение при старте приложения: ловим событие установки и клики по плашке */
export function initInstallBanner(): void {
  const u = new URL(location.href);
  if (u.searchParams.has('install')) {
    u.searchParams.delete('install');
    history.replaceState(history.state, '', u.href);
    autoInstall = !isInstalledPwa();
  }
  if (isInstalledPwa()) return;
  if (snoozed()) closed = true; // плашку не показываем, но пункт меню остаётся
  if (autoInstall) {
    closed = false; // сюда пришли ради установки — плашка нужна, даже если её недавно закрывали
    setTimeout(() => {
      if (!autoInstall) return;
      autoInstall = false;
      autoCb?.('missing'); // события так и нет: как правило, приложение уже установлено
    }, 4000);
  }
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    update();
    if (autoInstall) {
      autoInstall = false;
      autoCb?.('ready');
    }
  });
  window.addEventListener('appinstalled', () => {
    installed = true;
    deferred = null;
    update();
  });
  el()?.addEventListener('click', (e) => {
    const t = e.target instanceof Element ? e.target.closest<HTMLElement>('[data-install]') : null;
    if (!t) return;
    e.stopPropagation();
    if (t.dataset['install'] === 'close') dismiss();
    else void go();
  });
  update();
}
